import React, { useState, useRef, useEffect } from "react";
import {
  EntryBase,
  Arrow,
  HeaderBase,
  HeaderText,
  Content,
  Image,
} from "./list-entry.styled";
import { PrismicImageField } from "../../types/graphql";
import { htmlSerializer } from "../../utils/htmlSerializer";
import { PrismicRichText } from "@prismicio/react";
import { debounce } from "../../utils/debounce";
import * as FA from "../../styles/fontawesome";

interface IEntryProps {
  header: string | null | undefined;
  content: any;
  entryId: number;
  activeEntry: { id: number; isOpen: boolean };
  setActiveEntry: React.Dispatch<
    React.SetStateAction<{
      id: number;
      isOpen: boolean;
    }>
  >;
  listHeight: number;
  image?: PrismicImageField | null;
}

const Entry = (props: IEntryProps) => {
  const isActive = props.entryId === props.activeEntry.id;
  const isOpen = isActive && props.activeEntry.isOpen;

  // Calculate height of content div. Used for open/close transistion
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const height = contentRef.current?.clientHeight;
    height && setContentHeight(height);

    const handleResize = debounce(() => {
      const height = contentRef.current?.clientHeight;
      height && setContentHeight(height);
    }, 200);

    window.addEventListener("resize", handleResize, { passive: true });
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [contentHeight]);

  return (
    <EntryBase
      isActive={isActive}
      isAdjacentActive={props.activeEntry.id === props.entryId + 1}
    >
      <HeaderBase
        isOpen={isOpen}
        onClick={() =>
          isActive
            ? props.setActiveEntry({ ...props.activeEntry, isOpen: !isOpen })
            : props.setActiveEntry({ id: props.entryId, isOpen: true })
        }
      >
        {props.header && <HeaderText>{props.header}</HeaderText>}
        <Arrow isOpen={isOpen} css={FA.AngleDownSolid} />
      </HeaderBase>
      <Content
        isOpen={isOpen}
        contentHeight={contentHeight}
        listHeight={props.listHeight}
      >
        <div ref={contentRef}>
          {props.image?.url && (
            <Image
              isOpen={isOpen}
              src={props.image.url}
              alt={props.image.alt || "Xometry Image"}
              height={props.image.dimensions?.height}
              width={props.image.dimensions?.width}
            />
          )}
          {props.content && (
            <PrismicRichText
              field={props.content}
              components={htmlSerializer}
            />
          )}
        </div>
      </Content>
    </EntryBase>
  );
};

export default Entry;
